import './NotificationCenter.scss';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../../api/hooks/notifications/useNotifications';
import {
  AdminNotificationData,
  AiVisibilityReportReadyNotificationData,
  AppNotification,
  NotificationType,
  ProjectReadyNotificationData,
  ResolvedAdminNotification,
  resolveAdminNotification,
} from '../../../types/notifications/Notification';
import { getNotificationText } from '../../../utils/notifications/notificationText';
import { AI_VISIBILITY_REPORT, PROJECTS, SUBSCRIPTION } from '../../../routes/path';
import { playNotificationSound } from '../../../helpers/notificationSound';
import PausedMonitorsPopup from '../paused-monitors-popup/PausedMonitorsPopup';
import AdminNotificationPopup from '../admin-notification-popup/AdminNotificationPopup';

/** One-shot alerts dismissed only on click (not when the bell opens). */
const CLICK_TO_DISMISS_TYPES: NotificationType[] = [
  'PROJECT_READY',
  'AI_VISIBILITY_REPORT_READY',
];

const NotificationCenter: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const menuRef = useRef<Menu>(null);
  const { notifications, unreadCount, isLoading, markSeen } = useNotifications();
  const [pausedPopupOpen, setPausedPopupOpen] = useState(false);
  const [adminPopup, setAdminPopup] = useState<ResolvedAdminNotification | null>(null);

  // Play a gentle sound when a NEW notification arrives (unread count grows after the first load).
  const prevUnread = useRef(0);
  const soundArmed = useRef(false);
  useEffect(() => {
    if (isLoading) return;
    if (!soundArmed.current) {
      // First successful load — set the baseline without playing for pre-existing notifications.
      soundArmed.current = true;
      prevUnread.current = unreadCount;
      return;
    }
    if (unreadCount > prevUnread.current) {
      playNotificationSound();
    }
    prevUnread.current = unreadCount;
  }, [unreadCount, isLoading]);

  const badgeLabel = unreadCount > 9 ? '9+' : unreadCount > 0 ? String(unreadCount) : null;

  // The bell dot takes the colour of the most severe unread notification.
  const LEVEL_RANK: Record<string, number> = { CRITICAL: 3, WARNING: 2, INFO: 1 };
  const topLevel = notifications
    .filter((n) => !n.seen)
    .reduce<string>((max, n) => {
      const level = (n.level ?? 'INFO').toUpperCase();
      return (LEVEL_RANK[level] ?? 0) > (LEVEL_RANK[max] ?? 0) ? level : max;
    }, 'INFO');

  const handleScrollClose = useCallback(() => {
    menuRef.current?.hide({} as React.SyntheticEvent);
  }, []);

  useEffect(() => {
    document.addEventListener('scroll', handleScrollClose, true);
    return () => document.removeEventListener('scroll', handleScrollClose, true);
  }, [handleScrollClose]);

  const handleBellClick = (event: React.SyntheticEvent) => {
    menuRef.current?.toggle(event);

    // Mark currently-shown notifications as seen when the panel opens (by per-item key).
    // Click-to-dismiss types stay until the user opens the target page (see click handlers).
    const unseenKeys = notifications
      .filter((n) => !n.seen && !CLICK_TO_DISMISS_TYPES.includes(n.type))
      .map((n) => n.key ?? n.type);
    if (unseenKeys.length > 0) {
      markSeen(unseenKeys);
    }
  };

  const handleAdminClick = (data: AdminNotificationData) => {
    const resolved = resolveAdminNotification(data, i18n.language);
    if (data.hasPopup && resolved.description) {
      menuRef.current?.hide({} as React.SyntheticEvent);
      setAdminPopup(resolved);
    } else if (resolved.ctaUrl) {
      window.open(resolved.ctaUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handlePausedMonitorsClick = () => {
    menuRef.current?.hide({} as React.SyntheticEvent);
    setPausedPopupOpen(true);
  };

  const handleGoToSubscription = () => {
    menuRef.current?.hide({} as React.SyntheticEvent);
    navigate(SUBSCRIPTION);
  };

  const handleProjectReadyClick = (projectId: number) => {
    menuRef.current?.hide({} as React.SyntheticEvent);
    // Dismiss it now that the user is opening the project — it won't come back on the next poll.
    markSeen([`PROJECT_READY_${projectId}`]);
    navigate(`${PROJECTS}/${projectId}`);
  };

  const handleAiVisibilityReportReadyClick = (
    data: AiVisibilityReportReadyNotificationData
  ) => {
    menuRef.current?.hide({} as React.SyntheticEvent);
    markSeen([`AI_VISIBILITY_REPORT_READY_${data.reportId}`]);
    navigate(`${AI_VISIBILITY_REPORT}/${data.projectId}`);
  };

  // Built-in notifications keep their own icon + click target. Only admin notifications use the
  // level icon and level colour.
  const LEVEL_ICON: Record<string, string> = {
    INFO: 'ico--info',
    WARNING: 'ico--warning',
    // Same warning triangle as WARNING, but coloured red via the --critical class.
    CRITICAL: 'ico--warning',
  };

  const TYPE_CONFIG: Partial<Record<NotificationType, { icon: string; onClick: () => void }>> = {
    PAUSED_MONITORS: { icon: 'ico--pause', onClick: handlePausedMonitorsClick },
    CARD_EXPIRING: { icon: 'ico--card', onClick: handleGoToSubscription },
    PAYMENT_REMINDER: { icon: 'ico--calendar', onClick: handleGoToSubscription },
  };

  const renderNotificationItem = (notification: AppNotification) => {
    const isAdmin = notification.type === 'ADMIN';
    const level = (notification.level ?? 'INFO').toUpperCase();

    let icon: string;
    let onClick: () => void;

    if (isAdmin) {
      const data = notification.data as unknown as AdminNotificationData;
      icon = LEVEL_ICON[level] ?? LEVEL_ICON.INFO;
      onClick = () => handleAdminClick(data);
    } else if (notification.type === 'PROJECT_READY') {
      const data = notification.data as unknown as ProjectReadyNotificationData;
      icon = 'ico--folder';
      onClick = () => handleProjectReadyClick(data.projectId);
    } else if (notification.type === 'AI_VISIBILITY_REPORT_READY') {
      const data = notification.data as unknown as AiVisibilityReportReadyNotificationData;
      icon = 'ico--ai-visibility-report';
      onClick = () => handleAiVisibilityReportReadyClick(data);
    } else {
      const config = TYPE_CONFIG[notification.type];
      if (!config) {
        return null;
      }
      icon = config.icon;
      onClick = config.onClick;
    }

    return (
      <button
        key={notification.key ?? notification.type}
        className={`notification-center__item${
          isAdmin ? ` notification-center__item--${level.toLowerCase()}` : ''
        }${!notification.seen ? ' notification-center__item--unread' : ''}`}
        onClick={onClick}
        type="button"
      >
        <span className={`ico ${icon} ico--sm notification-center__item-icon`} />
        <span className="notification-center__item-text">
          {getNotificationText(t, notification, i18n.language)}
        </span>
      </button>
    );
  };

  const panelContent = (
    <div className="notification-center__panel">
      <div className="notification-center__header">
        <span className="notification-center__title">{t('notificationCenterTitle')}</span>
      </div>
      <div className="notification-center__list">
        {notifications.length === 0 ? (
          <div className="notification-center__empty">
            <div className="notification-center__empty-icon">
              <span className="notification-center__empty-spark notification-center__empty-spark--1" />
              <span className="notification-center__empty-spark notification-center__empty-spark--2" />
              <span className="notification-center__empty-spark notification-center__empty-spark--3" />
              <span className="notification-center__empty-spark notification-center__empty-spark--4" />
              <div className="notification-center__empty-circle">
                <span className="ico ico--bell ico--md" />
              </div>
            </div>
            <div className="notification-center__empty-title">{t('notificationCenterEmpty')}</div>
            <div className="notification-center__empty-text">
              {t('notificationCenterEmptyDesc')}
            </div>
          </div>
        ) : (
          notifications.map(renderNotificationItem)
        )}
      </div>
    </div>
  );

  return (
    <>
      <div className="notification-center">
        <Button
          className={`notification-center__bell${
            unreadCount > 0 ? ' notification-center__bell--ringing' : ''
          }`}
          onClick={handleBellClick}
        >
          <span className="ico ico--bell ico--sm" />
          {badgeLabel !== null && (
            <span
              className={`notification-center__badge notification-center__badge--${topLevel.toLowerCase()}`}
            />
          )}
        </Button>
        <Menu
          ref={menuRef}
          model={[{ template: () => panelContent }]}
          popup
          appendTo={document.body}
          baseZIndex={10000}
          className="notification-center__menu"
        />
      </div>

      <PausedMonitorsPopup
        visible={pausedPopupOpen}
        onHide={() => setPausedPopupOpen(false)}
      />

      <AdminNotificationPopup
        notification={adminPopup}
        visible={adminPopup !== null}
        onHide={() => setAdminPopup(null)}
      />
    </>
  );
};

export default NotificationCenter;
